import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import { FlexBox, Typography, AbsoluteBox, InputField, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { Keyboard } from 'components/Keyboard';
import { ProcessingOverlay } from 'components/ProcessingOverlay';
import { useActions } from 'hooks/useActions';
import { useKeyboard } from 'hooks/useKeyboard';
import { selectors as cartSelectors, actions as cartActions } from 'services/cart';
import { selectors as configSelectors, Configuration, Messages } from 'services/configuration';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { useStyles } from './styled';

const PromoCodeEnterView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { path, style } = useParams<{ path: string; style: string }>();
  const pathStyle = `${path}-${style}`;

  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary', '');
  const cartLabel = get(configuration, `configuration.labels.cart.${i18n.language}`) || t('cart.cartLabel');
  const priceTypePromoMessages: Messages = get(configuration, `${ticketingPath}.promoCode.priceType.messages`, {});
  const cartPromoMessages: Messages = get(configuration, `${ticketingPath}.promoCode.cart.messages`, {});
  const giftVoucherMessages: Messages = get(configuration, `${ticketingPath}.cart.giftVoucher.messages`, {});

  const [value, setValue] = useKeyboard('');
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [shouldRedirect, setShouldRedirect] = useState<boolean>(false);

  const classes = useStyles({ fontFamily })();

  const getActions = () => {
    let actionObj = null;
    switch (pathStyle) {
      case 'price-type-promo':
        actionObj = {
          applyPromoCode: ticketingActions.applyPriceTypePromoCode,
          resetPromoCode: ticketingActions.resetPriceTypePromoCode
        };
        break;
      case 'cart-gift-voucher':
        actionObj = {
          applyPromoCode: cartActions.applyGiftVoucher,
          resetPromoCode: cartActions.resetGiftVoucher
        };
        break;
      case 'cart-promo':
        actionObj = {
          applyPromoCode: cartActions.applyPromoCode,
          resetPromoCode: cartActions.resetPromoCode
        };
        break;
      default:
        actionObj = null;
    }

    return actionObj;
  };

  const getSelectors = () => {
    if (pathStyle === 'price-type-promo') {
      return ticketingSelectors.selectPriceTypePromoCode;
    } else if (pathStyle === 'cart-gift-voucher') {
      return cartSelectors.selectGiftVoucher;
    } else if (pathStyle === 'cart-promo') {
      return cartSelectors.selectPromoCode;
    }

    return ticketingSelectors.selectPriceTypePromoCode;
  };

  const getAppliedSelectors = () => {
    if (pathStyle === 'price-type-promo') {
      return ticketingSelectors.selectAppliedPriceTypePromo;
    } else if (pathStyle === 'cart-gift-voucher') {
      return cartSelectors.selectGiftVoucherCode;
    } else if (pathStyle === 'cart-promo') {
      return cartSelectors.selectPromoCodeString;
    }

    return ticketingSelectors.selectAppliedPriceTypePromo;
  };

  const { applyPromoCode, resetPromoCode } = useActions(getActions());
  const { loading, error, processed } = useSelector(getSelectors());
  const appliedCode = useSelector(getAppliedSelectors());

  React.useEffect(() => {
    resetPromoCode();
    if (appliedCode) setValue(appliedCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (shouldRedirect) {
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect]);

  const handleKeyboardClick = (_: React.MouseEvent<HTMLButtonElement>, v: string) => {
    setValue(v);
  };
  const handleSubmit = () => {
    setShowOverlay(true);
  };
  const handleTryAgain = () => {
    resetPromoCode();
    setShowOverlay(false);
    setShouldRedirect(false);
  };
  const handlePrevClick = () => {
    resetPromoCode();
    setShouldRedirect(true);
  };

  const getDescription = () => {
    switch (pathStyle) {
      case 'price-type-promo':
        return get(priceTypePromoMessages, `subheader.${i18n.language}`, t('promoCode.promoDescription'));
      case 'cart-gift-voucher':
        return get(giftVoucherMessages, `subheader.${i18n.language}`, t('promoCode.voucherDescription'));
      case 'cart-promo':
        return get(cartPromoMessages, `subheader.${i18n.language}`, t('promoCode.cartPromoDescription', { cartLabel }));
      default:
        return '';
    }
  };

  const getTitle = () => {
    switch (pathStyle) {
      case 'price-type-promo':
        return get(priceTypePromoMessages, `header.${i18n.language}`, t('promoCode.promoTitle'));
      case 'cart-gift-voucher':
        return get(giftVoucherMessages, `header.${i18n.language}`, t('promoCode.voucherTitle'));
      case 'cart-promo':
        return get(cartPromoMessages, `header.${i18n.language}`, t('promoCode.promoTitle'));
      default:
        return '';
    }
  };

  const loadingText = style === 'promo' ? t('promoCode.promoLoadingText') : t('promoCode.voucherLoadingText');
  const errorHeader = style === 'promo' ? t('promoCode.invalidCode') : error;
  const errorSubheader = style === 'promo' ? t('promoCode.promoErrorText') : t('promoCode.voucherErrorText');

  if (error && showOverlay) setShowOverlay(false);

  return (
    <>
      {error && !loading && (
        <ActionOverlay
          title={<Typography className={classes.promoErrorTitle}>{errorHeader}</Typography>}
          description={<Typography className={classes.promoErrorDescription}>{errorSubheader}</Typography>}
          confirmText={t('commonText.tryAgain')}
          onConfirm={handleTryAgain}
        />
      )}
      {showOverlay && (
        <ProcessingOverlay
          processed={processed}
          processAction={() => setTimeout(() => applyPromoCode(value), 1000)}
          title={loadingText}
          onAnimationComplete={() => {
            if (processed) setShouldRedirect(true);
          }}
          error={!!error}
        />
      )}
      <Grid className={classes.promoCodeContainer}>
        <Grid container direction="column" className={classes.promoCodeInnerContainer}>
          <FlexBox fullWidth align="center" justify="center" direction="column">
            <MuiBox className={classes.promoTitle}>
              <Typography fontSize={72} fontWeight={800} align="center">
                {getTitle()}
              </Typography>
            </MuiBox>
            <MuiBox className={classes.promoDescription}>
              <Typography fontSize={30} align="center">
                {getDescription()}
              </Typography>
            </MuiBox>
            <MuiBox className={classes.promoCodeInput}>
              <InputField
                autoFocus
                size="large"
                align="center"
                name="value"
                fullWidth
                data-testid="input-field"
                value={value}
              />
            </MuiBox>
          </FlexBox>
          <MuiBox className={classes.promoCodeKeyboardBox}>
            <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
              <FlexBox fullHeight align="center" direction="column">
                <Keyboard
                  language={i18n.language}
                  onClick={handleKeyboardClick}
                  onNext={handleSubmit}
                  onPrev={handlePrevClick}
                  isLoading={loading}
                  isNextDisabled={value.length === 0}
                  nextText={t('commonText.apply')}
                  prevText={t('commonText.back')}
                />
              </FlexBox>
            </AbsoluteBox>
          </MuiBox>
        </Grid>
      </Grid>
    </>
  );
};

export default PromoCodeEnterView;
